import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Spinner } from "@phosphor-icons/react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("pending");
  const [data, setData] = useState(null);
  const { clear } = useCart();
  const navigate = useNavigate();
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data: d } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        setData(d);
        if (d.payment_status === "paid") { setStatus("paid"); clear(); return; }
        if (d.status === "expired") { setStatus("expired"); return; }
        attempts.current += 1;
        if (attempts.current < 10) setTimeout(poll, 2000);
        else setStatus("timeout");
      } catch {
        if (attempts.current < 10) setTimeout(poll, 2000);
        else setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, clear]);

  return (
    <div className="max-w-2xl mx-auto" data-testid="checkout-success-page">
      <Card className="p-12 rounded-sm border-border text-center">
        {status === "pending" && (<>
          <Spinner size={48} className="mx-auto animate-spin text-[var(--uc-primary)]" />
          <h2 className="font-display font-black text-2xl mt-4">Confirming your payment…</h2>
          <p className="text-sm text-muted-foreground mt-2">Hold tight, this only takes a few seconds.</p>
        </>)}
        {status === "paid" && (<>
          <CheckCircle size={64} weight="duotone" className="mx-auto text-emerald-500" />
          <h2 className="font-display font-black text-3xl mt-4" data-testid="payment-success">Payment successful</h2>
          <p className="text-sm text-muted-foreground mt-2">${data?.amount?.toFixed(2)} {data?.currency?.toUpperCase()} has been charged. Your order is now in fulfillment.</p>
          <Button className="mt-6 rounded-sm bg-[var(--uc-primary)] hover:bg-[var(--uc-primary-hover)] text-white" onClick={() => navigate("/orders")} data-testid="view-orders-btn">View orders</Button>
        </>)}
        {(status === "expired" || status === "error" || status === "timeout") && (<>
          <XCircle size={64} weight="duotone" className="mx-auto text-destructive" />
          <h2 className="font-display font-black text-2xl mt-4">Payment {status === "expired" ? "expired" : "could not be confirmed"}</h2>
          <p className="text-sm text-muted-foreground mt-2">Please try again or contact your Partner Success Manager.</p>
          <Button className="mt-6 rounded-sm" variant="outline" onClick={() => navigate("/cart")}>Back to cart</Button>
        </>)}
      </Card>
    </div>
  );
}
